import type { UiContainer, UiNode, UiNodeInputAttributes, UiText } from "@ory/client-fetch";
import { Alert, Button, Field, Input, cn } from "@payezy/ui";
import { useEffect, useMemo, useRef } from "react";

type Attrs = Record<string, unknown> & { node_type: string };

function attrs(n: UiNode): Attrs {
  return n.attributes as unknown as Attrs;
}

function label(n: UiNode): string | undefined {
  return n.meta?.label?.text;
}

const groupTitles: Record<string, string> = {
  password: "Password",
  code: "Email code",
  totp: "Authenticator app",
  lookup_secret: "Backup codes",
  passkey: "Passkey",
  webauthn: "Security key",
  profile: "Profile",
};

export function Messages({ messages, className }: { messages?: UiText[]; className?: string }) {
  if (!messages?.length) return null;
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {messages.map((m) => (
        <Alert key={`${m.id}-${m.text}`} tone={m.type === "error" ? "danger" : m.type === "success" ? "success" : "info"}>
          {m.text}
        </Alert>
      ))}
    </div>
  );
}

/** Loads Kratos-provided scripts (WebAuthn / passkeys) once. */
function useScripts(nodes: UiNode[]) {
  useEffect(() => {
    const added: HTMLScriptElement[] = [];
    for (const n of nodes) {
      const a = attrs(n);
      if (a.node_type !== "script") continue;
      const id = String(a.id ?? a.src);
      if (document.getElementById(id)) continue;
      const s = document.createElement("script");
      s.id = id;
      s.src = String(a.src);
      s.async = Boolean(a.async ?? true);
      if (a.integrity) s.integrity = String(a.integrity);
      if (a.crossorigin) s.crossOrigin = String(a.crossorigin);
      if (a.referrerpolicy) s.referrerPolicy = String(a.referrerpolicy);
      if (a.type) s.type = String(a.type);
      if (a.nonce) s.nonce = String(a.nonce);
      document.body.appendChild(s);
      added.push(s);
    }
    return () => added.forEach((s) => s.remove());
  }, [nodes]);
}

function callTrigger(name: unknown) {
  if (typeof name !== "string" || !name) return;
  const fn = (window as unknown as Record<string, unknown>)[name];
  if (typeof fn === "function") (fn as () => void)();
}

function NodeView({ node }: { node: UiNode }) {
  const a = attrs(node);
  const err = node.messages?.find((m) => m.type === "error")?.text;
  switch (a.node_type) {
    case "input": {
      const ia = a as unknown as UiNodeInputAttributes & { node_type: string; onclickTrigger?: string; onloadTrigger?: string };
      if (ia.type === "hidden") return <input type="hidden" name={ia.name} value={String(ia.value ?? "")} />;
      if (ia.type === "submit" || ia.type === "button") {
        const variant = node.group === "default" || node.group === "password" || node.group === "code" || node.group === "totp" || node.group === "lookup_secret" ? "primary" : "secondary";
        return (
          <Button
            type={ia.type === "submit" ? "submit" : "button"}
            name={ia.name}
            value={String(ia.value ?? "")}
            disabled={ia.disabled}
            variant={ia.name === "lookup_secret_disable" || ia.name === "totp_unlink" ? "danger" : variant}
            className="w-full"
            onClick={ia.onclickTrigger ? (e) => { if (ia.type !== "submit") e.preventDefault(); callTrigger(ia.onclickTrigger); } : undefined}
          >
            {label(node) ?? "Continue"}
          </Button>
        );
      }
      if (ia.type === "checkbox") {
        return (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" name={ia.name} value="true" defaultChecked={Boolean(ia.value)} disabled={ia.disabled} />
            {label(node)}
          </label>
        );
      }
      return (
        <Field label={label(node)} error={err}>
          <Input
            name={ia.name}
            type={ia.type}
            defaultValue={ia.value === undefined || ia.value === null ? undefined : String(ia.value)}
            required={ia.required}
            disabled={ia.disabled}
            autoComplete={ia.autocomplete}
            aria-invalid={Boolean(err)}
            pattern={ia.pattern}
            maxLength={ia.maxlength}
          />
        </Field>
      );
    }
    case "img":
      return <img src={String(a.src)} alt={label(node) ?? ""} width={Number(a.width) || 200} height={Number(a.height) || 200} className="mx-auto rounded-md ring-1 ring-ink-200" />;
    case "text": {
      const t = a.text as UiText & { context?: { secrets?: UiText[] } };
      const secrets = t.context?.secrets;
      return (
        <div className="rounded-lg bg-ink-50 p-3 text-sm">
          {label(node) && <p className="mb-1 text-xs font-medium text-ink-500">{label(node)}</p>}
          {secrets ? (
            <div className="grid grid-cols-2 gap-1 font-mono text-[13px]">{secrets.map((s) => <span key={s.text}>{s.text}</span>)}</div>
          ) : (
            <p className="break-all font-mono text-[13px]">{t.text}</p>
          )}
        </div>
      );
    }
    case "a":
      return (
        <a className="text-sm text-brand-600 hover:underline" href={String(a.href)}>
          {(a.title as UiText | undefined)?.text ?? String(a.href)}
        </a>
      );
    default:
      return null;
  }
}

/**
 * Renders a Kratos UI container as a native form posting straight to Kratos, so CSRF,
 * redirects, and errors follow Kratos semantics exactly. `groups` limits which method
 * groups are shown (settings renders one form per group).
 */
export function FlowForm({ ui, groups, className }: { ui: UiContainer; groups?: string[]; className?: string }) {
  const nodes = useMemo(
    () => ui.nodes.filter((n) => !groups || n.group === "default" || groups.includes(n.group)),
    [ui.nodes, groups],
  );
  useScripts(nodes);
  const loaded = useRef(false);
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    for (const n of nodes) {
      const t = (attrs(n) as { onloadTrigger?: string }).onloadTrigger;
      if (t) setTimeout(() => callTrigger(t), 50);
    }
  }, [nodes]);

  const visible = nodes.filter((n) => attrs(n).node_type !== "script");
  const ordered = [...visible].sort((x, y) => weight(x) - weight(y));
  const sections = new Map<string, UiNode[]>();
  for (const n of ordered) {
    const g = n.group === "default" ? "default" : n.group;
    if (!sections.has(g)) sections.set(g, []);
    sections.get(g)!.push(n);
  }
  const hidden = ordered.filter((n) => n.group === "default" && (attrs(n) as { type?: string }).type === "hidden");
  const methodGroups = [...sections.keys()].filter((g) => g !== "default");

  return (
    <form action={ui.action} method={ui.method} className={cn("flex flex-col gap-4", className)}>
      {hidden.map((n, i) => <NodeView key={`h${i}`} node={n} />)}
      {(sections.get("default") ?? [])
        .filter((n) => !hidden.includes(n))
        .map((n, i) => <NodeView key={`d${i}`} node={n} />)}
      {methodGroups.map((g, gi) => (
        <div key={g} className="flex flex-col gap-3">
          {methodGroups.length > 1 && (
            <div className="flex items-center gap-3 pt-1 text-xs uppercase tracking-wide text-ink-400">
              {gi > 0 && <span className="h-px flex-1 bg-ink-200" />}
              <span>{groupTitles[g] ?? g}</span>
              <span className="h-px flex-1 bg-ink-200" />
            </div>
          )}
          {sections.get(g)!.map((n, i) => <NodeView key={`${g}${i}`} node={n} />)}
        </div>
      ))}
    </form>
  );
}

function weight(n: UiNode): number {
  const a = attrs(n) as { type?: string; node_type: string };
  if (a.node_type === "img" || a.node_type === "text") return 1;
  if (a.type === "submit" || a.type === "button") return 3;
  return 2;
}
